/**
 * HTTP Client with Bearer token auth.
 *
 * Auth strategy:
 * - Access token: Bearer header (from Zustand store).
 * - Refresh token: httpOnly cookie. The single-host build is served from
 *   the same origin as the API (both behind the env's GCP LB), so the
 *   cookie travels same-origin. Explicit `credentials: 'include'` only on
 *   the two endpoints that need it — `/auth/refresh` and the initial
 *   auth-init `/me`. Regular requests authenticate via the Bearer
 *   header, no cookie needed. The default `credentials: 'omit'` keeps the
 *   request simple and avoids an extra preflight-variant cache entry per
 *   route. (Cookie Domain/SameSite are still set server-side so the
 *   care-planner.<env> Firebase app can share the session — see api-service-go.)
 *
 * Routing (single-host):
 * - BASE_URL defaults to the same-origin relative path `/api/v1`. The
 *   primary app is served by the env's GCP Global HTTPS LB, whose URL-map
 *   splits `/api/**` → Cloud Run (api-service-go) and `/**` → the React
 *   build in GCS. Same origin for both regular API calls and SSE streams —
 *   no Firebase Hosting in the path, so nothing buffers SSE.
 * - VITE_API_URL is an optional override (e.g. local dev points it at a
 *   different host, or a future env routes the API elsewhere). When unset
 *   in CI the production build is fully same-origin.
 */

import { useAuthStore } from "../stores/auth";
import { reconcileIdentity, tokenConflictsWithUser } from "@/lib/authIdentity";
import { createLogger } from "@/utils/logger";
import {
  COLD_START_EMPTY_BODY_DELAY_MS,
  COLD_START_503_DEFAULT_DELAY_MS,
} from "@/lib/constants";
import { parseRetryAfter } from "@/lib/httpUtils";
import { isRecord } from "@/lib/isRecord";
import i18n from "@/i18n";
import { getApiBaseUrl } from "./baseUrl";
import type { ZodType } from "zod";
import { nextPagePath } from "./paging";
import { parseResponse } from "./validation";

const logger = createLogger("API");

const BASE_URL = getApiBaseUrl(); // Use API_URL for useBackendHealth.ts

/**
 * HTTP status codes that indicate a transient server state during cold start.
 * These are safe to retry once. Client errors (4xx) and server bugs (500) are
 * explicitly excluded — retrying them would mask problems or waste the user's time.
 *
 * Note: TypeError network errors (DNS hiccup, mid-flight drop, wifi handoff)
 * are intentionally NOT retried here. Retry belongs at the caller layer
 * (React Query is configured with `retry: 2` in App.tsx), which has proper
 * jitter, retry budget, and per-query opt-out. A second retry layer in the
 * HTTP client would amplify outages without adding safety.
 */
const RETRYABLE_STATUSES = new Set<number>([503]);

/** Generate a unique request ID for distributed tracing. */
export function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Route of the DEV-only design-preview surface in the satellite apps (e.g.
 * apps/compatibility). Single-sourced here so the route gate (App.tsx) and
 * the 401-redirect exemption below can never disagree on the path.
 */
export const DEV_PREVIEW_PATH = "/preview";

/**
 * DEV-only design-preview surface (`DEV_PREVIEW_PATH` in the satellite
 * apps, e.g. apps/compatibility). It renders against build-time fixtures
 * without auth by design, so a 401 from an enrichment endpoint is the
 * expected state there — hijacking the page to /login would white-screen
 * the harness. Errors still throw and surface as the calling component's
 * error state. `import.meta.env.DEV` is false in production builds, so
 * this is dead-code-eliminated and the /login redirect is unconditional
 * in prod.
 */
export function isDevPreviewSurface(): boolean {
  return import.meta.env.DEV && window.location.pathname === DEV_PREVIEW_PATH;
}

/**
 * Endpoints where a 401 IS the answer to the request, not evidence that an
 * existing session expired. You are not signed in when you call these — that
 * is the point of calling them.
 *
 * The generic 401 handler below assumes "expired session": refresh, and on
 * failure log out and hard-navigate to /login. Applied to a sign-in attempt
 * that is the wrong reaction and it destroys the answer — a nurse who mistypes
 * her password triggers a full page reload, which tears down the React state
 * holding the error, so the form silently blanks itself and tells her nothing.
 * The endpoint answered clearly; the transport threw the answer away.
 */
const AUTH_ENTRY_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/verify-email",
  "/auth/resend-verification",
  "/auth/oauth/",
];

/** True for a path whose own 401 must be returned to the caller verbatim. */
function isAuthEntryPath(path: string): boolean {
  const route = path.split("?")[0];
  return AUTH_ENTRY_PATHS.some(
    (p) => route === p || route.startsWith(p),
  );
}

// Export for direct fetch calls (e.g., FormData uploads)
export const apiUrl = BASE_URL;

// SSE streaming uses the same same-origin base as everything else: the GCP
// LB's URL-map split routes /api/** to Cloud Run without any buffering
// proxy in front. Kept as a named export so streaming call sites stay
// decoupled from BASE_URL and a future env could re-point streaming alone.
export const streamingApiUrl = BASE_URL;

export class ApiError extends Error {
  /**
   * The failed call's HTTP method and API path ("POST", "/sessions"), stamped
   * at the single throw funnel in request() — every ApiError leaves through it.
   *
   * These exist so a reported error names the call that failed. The console
   * line has always carried the path; the "Report" beacon dropped it and sent
   * only the browser path, which is why a PATIENT_FORBIDDEN report read as
   * "…cannot access — /home" and could not be triaged.
   *
   * Optional because an ApiError built outside the funnel (a test double, a
   * hand-thrown parse failure) has no call to name — absent, never faked.
   */
  public method?: string;
  public path?: string;

  /**
   * The failed response's `X-Request-ID`. The server has always sent this
   * (tricorder-go/middleware.RequestID) and CORS has always exposed it "for
   * client-side observability" — nothing read it, so the id died at the client
   * boundary. Carrying it lets a reported error join 1:1 to that request's own
   * structured log line, which is the difference between knowing an endpoint
   * returned 403 and knowing why.
   *
   * Absent when no response was received (network failure, abort) — there is
   * no header to read, and inventing one client-side would correlate to
   * nothing.
   */
  public requestId?: string;

  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
    /**
     * RFC 9457 problem-body extension members (top-level keys alongside
     * type/title/status/detail/error) — e.g. a 403's `request_access`
     * remediation. Generic bag: readers validate the shape they expect
     * (see lib/requestAccess.ts). Undefined for non-problem/plain errors.
     */
    public extensions?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Wire codes minted by wrapFetchError below. Named so call sites branch on a
// constant instead of re-typing the literal (it was duplicated across five
// files, and a caller that spelled it wrong silently fell through to the
// "server rejected you" branch).
export const ERROR_CODE_NETWORK = "NETWORK_ERROR" as const;
export const ERROR_CODE_TIMEOUT = "TIMEOUT" as const;
export const ERROR_CODE_ABORTED = "ABORTED" as const;
export const ERROR_CODE_UNKNOWN = "UNKNOWN" as const;
export const ERROR_CODE_HTTP = "HTTP_ERROR" as const;

/**
 * True when the request never reached the server: the browser refused, dropped,
 * or timed out the connection, so `status` is 0 and there is no response.
 *
 * The distinction is not cosmetic. The server returned NO verdict, so a caller
 * must never render one — reporting a dead Wi-Fi link as "check your
 * credentials" (Login) or "no access" (a 403 branch) sends the user to fix
 * something that was never wrong. Ask: did the API actually answer? If not,
 * the only honest message is "we couldn't reach it".
 */
export function isTransportError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return (
    error.code === ERROR_CODE_NETWORK ||
    error.code === ERROR_CODE_TIMEOUT ||
    (error.code === ERROR_CODE_UNKNOWN && error.status === 0)
  );
}

/**
 * True when the server answered, but with a fault of its own (5xx). Also not a
 * verdict on the user's input — retry is the right action, blaming their
 * credentials is not.
 */
export function isServerFaultError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === ERROR_CODE_HTTP &&
    error.status >= 500
  );
}

// Standard RFC 9457 problem members — everything else on the body is an
// extension member the client surfaces on ApiError.extensions.
const STANDARD_PROBLEM_KEYS = new Set([
  "type",
  "title",
  "status",
  "detail",
  "instance",
  "error",
  "message",
  "code",
  "details",
]);

/** Collect the non-standard top-level keys of a parsed error body as extensions. */
function collectProblemExtensions(
  rawError: unknown,
): Record<string, unknown> | undefined {
  if (typeof rawError !== "object" || rawError === null) return undefined;
  const ext: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rawError as Record<string, unknown>)) {
    if (!STANDARD_PROBLEM_KEYS.has(k)) ext[k] = v;
  }
  return Object.keys(ext).length > 0 ? ext : undefined;
}

export interface RequestOptions extends RequestInit {
  timeoutMs?: number;
  /** Return the response body as raw text (skip JSON.parse) — for non-JSON
   *  representations like the text/plain run-recap narrative. */
  rawText?: boolean;
}

/**
 * Per-request headers shared by `request<T>` and `getNDJSON<T>`. Both
 * code paths need the same auth + tracing + i18n + caller-override merge
 * shape; extracting here so a future addition (e.g., a tracing header,
 * a tenant ID) lands once and applies to both.
 *
 * `acceptOverride` lets `getNDJSON` pin `Accept: application/x-ndjson`
 * without forcing the JSON path to think about Accept (the server's
 * routing for the JSON paths doesn't care). FormData bodies get no
 * Content-Type so the browser can supply the multipart boundary itself.
 */
function buildAuthedHeaders(
  fetchOptions: RequestInit,
  isFormData: boolean,
  acceptOverride?: string,
): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  const callerHeaders =
    fetchOptions.headers &&
    typeof fetchOptions.headers === "object" &&
    !Array.isArray(fetchOptions.headers)
      ? (fetchOptions.headers as Record<string, string>)
      : {};
  return {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(acceptOverride && { Accept: acceptOverride }),
    ...(token && { Authorization: `Bearer ${token}` }),
    "X-Request-ID": generateRequestId(),
    "Accept-Language": i18n.language,
    ...callerHeaders,
  };
}

/**
 * Normalize a non-OK Response into an ApiError. Handles both Tricorder's
 * `{error: {code, message, details?}}` shape AND FastAPI's `{detail: ...}`
 * shape so callers don't branch on the originating service. Returns a
 * fully-constructed ApiError that the caller throws.
 *
 * A non-JSON error body (HTML 502 page, plain-text gateway error, empty
 * body) is NOT a failure to recover from loudly — the HTTP status is the
 * signal. Build the ApiError from the status, using the raw text as the
 * message when present. No SyntaxError, no spammed parse-failure log.
 */
async function parseErrorResponse(response: Response): Promise<ApiError> {
  // Read once, apply to every exit: a non-JSON gateway page needs the
  // correlation id just as much as a well-formed problem body — arguably more.
  const requestId = response.headers.get("X-Request-ID") ?? undefined;
  const bodyText = await response.text();
  let rawError: unknown = {};
  if (bodyText.trim() !== "") {
    try {
      rawError = JSON.parse(bodyText);
    } catch {
      const parseFailed = new ApiError(
        ERROR_CODE_HTTP,
        bodyText.trim(),
        response.status,
      );
      parseFailed.requestId = requestId;
      return parseFailed;
    }
  }

  const body =
    typeof rawError === "object" && rawError !== null
      ? (rawError as Record<string, unknown>)
      : {};
  // Three body shapes reach here: the gateway's `{error: {code, message}}`,
  // FastAPI's `{detail: {...}}`, and a plain RFC 9457 problem
  // (`{type, title, status, detail, instance}`, what tricorder-protocol and
  // tricorder-studio answer), whose `detail` is a string and which names no
  // code. A problem without a code is an HTTP error the status describes, so it
  // is HTTP_ERROR, not UNKNOWN: UNKNOWN reads as "no answer at all".
  const errorObjTyped = isRecord(body.error)
    ? body.error
    : isRecord(body.detail)
      ? body.detail
      : {};

  const errorCode =
    typeof errorObjTyped.code === "string" ? errorObjTyped.code : ERROR_CODE_HTTP;
  const errorMessage =
    typeof errorObjTyped.message === "string"
      ? errorObjTyped.message
      : typeof body.detail === "string" && body.detail !== ""
        ? body.detail
        : typeof body.title === "string" && body.title !== ""
          ? body.title
          : "Request failed";
  const apiError = new ApiError(
    errorCode,
    errorMessage,
    response.status,
    errorObjTyped.details,
    collectProblemExtensions(rawError),
  );
  apiError.requestId = requestId;
  return apiError;
}

/**
 * Convert a fetch-layer thrown value into an ApiError. Both request paths
 * end in the same catch shape — AbortError (caller- or timeout-driven),
 * TypeError (network/CORS), ApiError pass-through, unknown wrapper.
 *
 * `timeoutMs` is threaded through so we can distinguish a caller-initiated
 * abort (returns ApiError("ABORTED")) from a timeout-driven abort (returns
 * ApiError("TIMEOUT")).
 */
function wrapFetchError(
  error: unknown,
  path: string,
  timeoutMs?: number,
): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error && error.name === "AbortError") {
    if (timeoutMs) {
      logger.error(`Request timeout after ${timeoutMs}ms: ${path}`);
      return new ApiError(ERROR_CODE_TIMEOUT, "Request timed out", 0);
    }
    logger.debug(`Request cancelled: ${path}`);
    return new ApiError(ERROR_CODE_ABORTED, "Request cancelled", 0);
  }
  if (error instanceof TypeError) {
    logger.error(`Network error: ${error.message} (Path: ${path})`);
    return new ApiError(ERROR_CODE_NETWORK, "Network error", 0);
  }
  logger.error(`Unknown error (Path: ${path})`, error);
  return new ApiError(
    ERROR_CODE_UNKNOWN,
    error instanceof Error ? error.message : "Request failed",
    0,
  );
}

/**
 * Set up an abort signal that fires after `timeoutMs` if provided. When
 * `timeoutMs` is undefined the returned signal is undefined and no timer
 * runs — matching the policy that cold starts are expected, not failures,
 * and only callers that explicitly opt-in get a timeout. The browser's
 * native connection timeout (60-120s) handles truly dead servers.
 *
 * Caller MUST invoke cleanup() in a `finally`. Calling cleanup() before
 * recursing into another request is also safe and releases the timer
 * eagerly — clearTimeout on an already-cleared timer is a no-op.
 */
function createAbortHandle(timeoutMs: number | undefined): {
  signal: AbortSignal | undefined;
  cleanup: () => void;
} {
  if (!timeoutMs) {
    return { signal: undefined, cleanup: () => {} };
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { signal: controller.signal, cleanup: () => clearTimeout(timeoutId) };
}


/** A parsed body and the response it came in, for readers that need headers. */
interface Received<T> {
  body: T;
  response: Response;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await requestWithResponse<T>(path, options)).body;
}

async function requestWithResponse<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<Received<T>> {
  const { timeoutMs, rawText, ...fetchOptions } = options;
  const abort = createAbortHandle(timeoutMs);

  try {
    // Don't set Content-Type for FormData (browser sets it with boundary)
    const isFormData = fetchOptions.body instanceof FormData;
    const response = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      credentials: fetchOptions.credentials ?? "omit",
      ...(abort.signal && { signal: abort.signal }),
      headers: buildAuthedHeaders(fetchOptions, isFormData),
    });

    if (!response.ok) {
      // --- Cold-start: 503 retry ---
      // Backend middleware signals "service is starting" via 503 + Retry-After.
      // Retry once after the specified delay (default 5s, capped at 10s).
      // Do NOT retry if: already a retry, status not in RETRYABLE_STATUSES,
      // or if the caller is doing a 401 refresh (isRetry protects that path too).
      if (RETRYABLE_STATUSES.has(response.status) && !isRetry) {
        const retryAfterHeader = response.headers.get("Retry-After");
        const retryAfterSeconds = parseRetryAfter(retryAfterHeader);
        const delayMs =
          retryAfterSeconds !== null
            ? retryAfterSeconds * 1000
            : COLD_START_503_DEFAULT_DELAY_MS;

        logger.warn(`503 on ${path}, retrying in ${delayMs}ms`, {
          path,
          retryAfterHeader,
          delayMs,
        });

        abort.cleanup();
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        return await requestWithResponse<T>(path, options, true);
      }

      // Handle 401 — attempt token refresh or clear auth (only once).
      // Done BEFORE parseErrorResponse so the refresh path skips a body
      // read we'd then discard. If refresh fails the logout side-effect
      // fires and we fall through to throw a fully-normalized ApiError.
      // EXCEPT on the OAuth callback page: there the code exchange owns
      // the session — a refresh here presents the pre-login cookie (the
      // guaranteed-401 race useAuthInit's guard exists to stop), and the
      // logout + /login redirect would kill the exchange mid-flight. The
      // error propagates to its caller's catch instead (the tier and
      // launchable fetches fail closed/open by design).
      if (
        response.status === 401 &&
        !isRetry &&
        !isAuthEntryPath(path) &&
        !window.location.pathname.startsWith("/auth/callback")
      ) {
        if (await recoverFromUnauthorized()) {
          return await requestWithResponse<T>(path, options, true);
        }
      }

      throw await parseErrorResponse(response);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { body: undefined as T, response };
    }

    // Read the full response body as text before attempting to parse.
    const text = await response.text();

    // --- Cold-start: empty 200 body retry ---
    // Cloud Run can accept the TCP connection before the process is fully
    // initialized, returning HTTP 200 with an empty body. The backend
    // middleware converts this to 503, but this is a defense-in-depth
    // fallback for any race window that slips through.
    //
    // Only retry once (isRetry guard) and only on the first occurrence.
    // Do not retry on a second empty body — that's a real error.
    //
    // NOTE: The 204 guard above MUST precede this check. A 204 response
    // always has an empty body; if the order were reversed, every 204
    // would trigger a spurious cold-start retry.
    if (text.trim() === "" && !isRetry) {
      logger.warn(
        `Empty 200 body on ${path}, retrying in ${COLD_START_EMPTY_BODY_DELAY_MS}ms`,
        { path, status: response.status },
      );
      abort.cleanup();
      await new Promise<void>((resolve) =>
        setTimeout(resolve, COLD_START_EMPTY_BODY_DELAY_MS),
      );
      return await requestWithResponse<T>(path, options, true);
    }

    // Raw-text representations (e.g. the text/plain run-recap narrative) return
    // the body verbatim — the caller opted out of JSON parsing.
    if (rawText) {
      return { body: text as unknown as T, response };
    }

    // Parse JSON response with error handling. The response body is
    // deliberately omitted from logs and the error message — when the
    // Content-Type was application/json the body may contain patient data,
    // and Sentry shouldn't see it. A dev reproducing this can capture the
    // body via the Network panel.
    try {
      return { body: JSON.parse(text) as T, response };
    } catch (parseError) {
      logger.error(`JSON parse error on ${path}`, {
        path,
        status: response.status,
        contentType: response.headers.get("Content-Type"),
        parseError,
      });
      const parseFailed = new ApiError(
        "PARSE_ERROR",
        "Failed to parse response",
        response.status,
      );
      // A 200 whose body we cannot parse is the case where the server log is
      // the ONLY account of what was sent — the body is deliberately never
      // logged here (it may carry patient data), so the correlation id is the
      // whole means of investigating it.
      parseFailed.requestId = response.headers.get("X-Request-ID") ?? undefined;
      throw parseFailed;
    }
  } catch (error) {
    const apiError = wrapFetchError(error, path, timeoutMs);
    // Stamp the call's identity here — the one funnel every ApiError exits
    // through — so any downstream reader (the "Report" beacon, a retry banner)
    // can name the endpoint that failed instead of re-deriving it from a log
    // string. Method defaults to GET exactly as fetch does.
    apiError.method = fetchOptions.method ?? "GET";
    apiError.path = path;
    if (
      apiError.code !== ERROR_CODE_TIMEOUT &&
      apiError.code !== ERROR_CODE_ABORTED &&
      apiError.code !== ERROR_CODE_NETWORK &&
      apiError.code !== ERROR_CODE_UNKNOWN
    ) {
      logger.error(
        `API error: ${apiError.code} - ${apiError.message} (Path: ${path})`,
      );
    }
    throw apiError;
  } finally {
    abort.cleanup();
  }
}

/**
 * The answer to a 401 for every caller that holds an access token: refresh it
 * once, and when that fails, end the session and send the user to /login.
 * Returns true when the caller may retry with the refreshed token. A caller
 * that retries on its own clock (a stream) MUST come here rather than
 * reconnecting with the token it already has: on 22 October 2026 a session
 * stream did that and sent staging 66,887 401s in one day.
 */
export async function recoverFromUnauthorized(): Promise<boolean> {
  if (await attemptTokenRefresh()) {
    return true;
  }
  useAuthStore.getState().logout();
  if (!isDevPreviewSurface()) {
    window.location.href = "/login";
  }
  return false;
}

/**
 * Attempt to refresh the access token using the httpOnly refresh cookie.
 * Uses a lock to prevent concurrent refresh attempts from racing.
 */
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        return false;
      }

      const data: unknown = await response.json();
      // Validate the wire shape before storing — a silent shape drift would
      // store `undefined` as the token and surface as a confusing 401 cascade
      // on the next request, with no obvious connection to the refresh.
      const accessToken = (data as Record<string, unknown>)?.access_token;
      if (typeof accessToken !== "string" || accessToken === "") {
        // Presence/shape metadata ONLY — never the body. This response is
        // credential-bearing (a token refresh); logging it verbatim would put
        // a bearer token (or whatever else the server sent) into the console
        // and any log drain (same PII discipline as the JSON-parse-error path
        // above, which omits the body for the same reason).
        logger.error("Refresh response missing access_token", {
          responseKeys:
            data && typeof data === "object" ? Object.keys(data as Record<string, unknown>) : [],
          receivedType: typeof accessToken,
        });
        return false;
      }
      // The domain-wide refresh cookie can belong to a different account than
      // the active profile (a sibling-subdomain login rewrites it). Roll the
      // token forward only when it belongs to the current user; otherwise
      // reconcile the profile to the token so the app never acts as one
      // account while displaying another.
      const { user } = useAuthStore.getState();
      if (!tokenConflictsWithUser(accessToken, user)) {
        useAuthStore.getState().setAccessToken(accessToken);
        return true;
      }
      return await reconcileIdentity(accessToken);
    } catch {
      return false;
    } finally {
      // Clear the lock after completion (success or failure)
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Result shape returned by `api.getNDJSON`. `items` holds the parsed rows;
 * `totalCount` is sourced from the `X-Total-Count` response header (the
 * server uses it to expose the total-matching count that can't be embedded
 * naturally in an NDJSON stream). `null` when the header is absent or
 * unparseable — callers that need pagination should treat it as "unknown".
 */
export interface NDJSONResult<T> {
  items: T[];
  totalCount: number | null;
}

/**
 * GET an `application/x-ndjson` endpoint. Parses the response body
 * line-by-line into an array of T, and pulls the total-count signal out of
 * the `X-Total-Count` response header.
 *
 * Shares the same auth + cold-start + abort scaffolding as the rest of
 * the `api.*` family by routing through `request<string>` with a Text
 * parser. The line-split + per-row parse happens once, here, so call sites
 * stay as one-liners.
 *
 * A malformed row THROWS (wrapped by the catch below, same as any other
 * fetch failure) rather than being logged and dropped — a row that doesn't
 * parse is a real contract break, and silently skipping it makes the
 * returned list shorter than `X-Total-Count` with no signal to the caller
 * that anything is missing. The server-side WriteNDJSON guarantee (a per-
 * item marshal failure doesn't break the stream's framing for later rows)
 * is about the wire staying well-formed, not about the client being allowed
 * to under-report what it received.
 */
async function getNDJSON<T>(
  path: string,
  options?: RequestOptions,
  isRetry = false,
): Promise<NDJSONResult<T>> {
  const { timeoutMs, ...fetchOptions } = options ?? {};
  const abort = createAbortHandle(timeoutMs);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      credentials: fetchOptions.credentials ?? "omit",
      ...(abort.signal && { signal: abort.signal }),
      headers: buildAuthedHeaders(fetchOptions, false, "application/x-ndjson"),
    });

    if (!response.ok) {
      // Same 401-refresh shape as request<T>. NDJSON-only endpoints
      // benefit from token refresh too — without this an expired token
      // on the documents list would 401 hard instead of silently rolling
      // over to the new token.
      if (response.status === 401 && !isRetry && !isAuthEntryPath(path)) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          abort.cleanup();
          return await getNDJSON<T>(path, options, true);
        }
        useAuthStore.getState().logout();
        if (!isDevPreviewSurface()) {
          window.location.href = "/login";
        }
      }
      throw await parseErrorResponse(response);
    }

    const totalCountHeader = response.headers.get("X-Total-Count");
    const totalCountParsed =
      totalCountHeader !== null && totalCountHeader !== ""
        ? Number.parseInt(totalCountHeader, 10)
        : NaN;
    const totalCount = Number.isFinite(totalCountParsed)
      ? totalCountParsed
      : null;

    return {
      items: await readNDJSONStream<T>(response, path),
      totalCount,
    };
  } catch (error) {
    throw wrapFetchError(error, path, timeoutMs);
  } finally {
    abort.cleanup();
  }
}

/**
 * Stream-decode the NDJSON response body. Reads chunks from the response
 * stream and emits parsed rows as complete lines arrive — JSON parsing
 * happens in lockstep with network I/O instead of after the full body has
 * been buffered into a string. The win is real on large lists (no
 * O(body) intermediate string allocation, and the JS main thread can yield
 * between chunks instead of blocking on one big parse at the end).
 *
 * Framing assumption: lines terminated by '\n', no CR. Matches what the
 * server's WriteNDJSON writes. CR would survive the split into the trim
 * branch below; whitespace-trim is the recovery for that edge case.
 *
 * A row that fails JSON.parse THROWS instead of being logged and dropped —
 * see the doc comment on getNDJSON for why. The caller's fetch wrapper turns
 * this into the same ApiError shape as a network/HTTP failure, so the query
 * lands in the ordinary error state (with retry) instead of quietly handing
 * back an undercounted list that still "succeeds".
 *
 * Fallback to text() when response.body is null — covers test environments
 * that don't expose a ReadableStream (jsdom historically lacked it; modern
 * versions implement it but the fallback keeps the function robust against
 * older test setups + edge-case fetch polyfills).
 */
async function readNDJSONStream<T>(
  response: Response,
  path: string,
): Promise<T[]> {
  const items: T[] = [];

  const pushLine = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed === "") return;
    try {
      items.push(JSON.parse(trimmed) as T);
    } catch (parseErr) {
      // Row content deliberately omitted from the log — NDJSON rows are
      // domain payloads and may contain patient data. The path names the
      // failure; the row itself is reproducible via the Network panel.
      // THROW rather than skip: a corrupt row is a real contract break, and
      // continuing would return a list silently shorter than X-Total-Count.
      logger.error(`NDJSON parse error on ${path}`, { path, parseError: parseErr });
      throw new Error(`NDJSON parse error on ${path}: malformed row`, { cause: parseErr });
    }
  };

  if (!response.body) {
    // No streaming reader available — degrade to the buffered path. Same
    // observable result, just without the streaming win. Hit by test
    // environments (some jsdom + fetch-polyfill combinations) more than
    // by real browsers.
    const text = await response.text();
    for (const line of text.split("\n")) pushLine(line);
    return items;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  // Buffer holds the in-flight trailing fragment between chunk reads —
  // an NDJSON record may span a chunk boundary so we can't blindly parse
  // each chunk. Each chunk: decode → append to buffer → split off all
  // complete lines (everything before the last '\n') → keep the tail.
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIdx = buffer.indexOf("\n");
      while (newlineIdx !== -1) {
        pushLine(buffer.slice(0, newlineIdx));
        buffer = buffer.slice(newlineIdx + 1);
        newlineIdx = buffer.indexOf("\n");
      }
    }
    // Flush the decoder's internal state in case the last chunk ended on
    // a partial multi-byte UTF-8 sequence, then push any final unterminated
    // line (server omits the trailing '\n' only on malformed output, but
    // we accept it to avoid silently dropping a row).
    buffer += decoder.decode();
    if (buffer.length > 0) pushLine(buffer);
  } finally {
    // Cancelling the reader on early exit avoids a half-open stream when
    // an exception (e.g., AbortError) interrupts the read loop.
    reader.releaseLock();
  }

  return items;
}

/**
 * Result shape returned by `api.getJSONWithTotal` — same {items, totalCount}
 * contract as `NDJSONResult` (totalCount sourced from the `X-Total-Count`
 * response header, `null` when absent/unparseable), for endpoints whose body
 * is a plain buffered JSON array rather than a streamed NDJSON body (e.g.
 * GET /users — see api-service-go/handlers.go HandleListUsers).
 */
export interface JSONWithTotalResult<T> {
  items: T[];
  totalCount: number | null;
}

/**
 * GET a bare-JSON-array endpoint that also carries an `X-Total-Count`
 * response header. Mirrors `getNDJSON`'s own fetch (rather than layering on
 * `request<T>()`, which has no path to return headers alongside the parsed
 * body) — same auth/401-refresh handling, JSON.parse instead of the NDJSON
 * line-stream reader.
 */
async function getJSONWithTotal<T>(
  path: string,
  options?: RequestOptions,
  isRetry = false,
): Promise<JSONWithTotalResult<T>> {
  const { timeoutMs, ...fetchOptions } = options ?? {};
  const abort = createAbortHandle(timeoutMs);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      credentials: fetchOptions.credentials ?? "omit",
      ...(abort.signal && { signal: abort.signal }),
      headers: buildAuthedHeaders(fetchOptions, false),
    });

    if (!response.ok) {
      // Same 401-refresh shape as request<T>/getNDJSON.
      if (response.status === 401 && !isRetry && !isAuthEntryPath(path)) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          abort.cleanup();
          return await getJSONWithTotal<T>(path, options, true);
        }
        useAuthStore.getState().logout();
        if (!isDevPreviewSurface()) {
          window.location.href = "/login";
        }
      }
      throw await parseErrorResponse(response);
    }

    const totalCountHeader = response.headers.get("X-Total-Count");
    const totalCountParsed =
      totalCountHeader !== null && totalCountHeader !== ""
        ? Number.parseInt(totalCountHeader, 10)
        : NaN;
    const totalCount = Number.isFinite(totalCountParsed)
      ? totalCountParsed
      : null;

    const text = await response.text();
    const items = text.trim() === "" ? [] : (JSON.parse(text) as T[]);

    return { items, totalCount };
  } catch (error) {
    throw wrapFetchError(error, path, timeoutMs);
  } finally {
    abort.cleanup();
  }
}

/**
 * Result shape returned by `api.getWithETag` — the parsed body plus the entity
 * tag the response carried.
 *
 * A conditional write needs the tag of the read it was computed from, and
 * `request<T>()` has no path to return headers alongside the parsed body. The
 * caller sends it back as `If-Match`, which is what stops a complete-view
 * replacement from silently overwriting an edit made in between.
 */
export interface JSONWithETagResult<T> {
  body: T;
  etag: string | null;
}

/**
 * GET a JSON endpoint and keep its `ETag`. Mirrors `getJSONWithTotal`'s own
 * fetch (same auth and 401-refresh handling) for the same reason: the shared
 * `request<T>()` returns only the body.
 */
async function getWithETag<T>(
  path: string,
  options?: RequestOptions,
  isRetry = false,
): Promise<JSONWithETagResult<T>> {
  const { timeoutMs, ...fetchOptions } = options ?? {};
  const abort = createAbortHandle(timeoutMs);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      credentials: fetchOptions.credentials ?? "omit",
      ...(abort.signal && { signal: abort.signal }),
      headers: buildAuthedHeaders(fetchOptions, false),
    });

    if (!response.ok) {
      if (response.status === 401 && !isRetry && !isAuthEntryPath(path)) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          abort.cleanup();
          return await getWithETag<T>(path, options, true);
        }
        useAuthStore.getState().logout();
        if (!isDevPreviewSurface()) {
          window.location.href = "/login";
        }
      }
      throw await parseErrorResponse(response);
    }

    const text = await response.text();
    return {
      body: (text.trim() === "" ? null : JSON.parse(text)) as T,
      etag: response.headers.get("ETag"),
    };
  } catch (error) {
    throw wrapFetchError(error, path, timeoutMs);
  } finally {
    abort.cleanup();
  }
}

/** A page as read: the body's rows and extras, plus the path of the next page. */
export type ReadPage<B> = B & { next: string | null };

/** Options of a list read: the request's own, plus the body's schema. */
export type PageOptions<B> = RequestOptions & { schema?: ZodType<B> };

/**
 * GET one page of a list. `next` is the path of the page after it, read from
 * the response's `Link: <...>; rel="next"` (RFC 8288), or null when this page
 * is the last. Pass `next` back to `getPage` to read on; never build it.
 */
async function getPage<B extends { items: readonly unknown[] }>(
  path: string,
  options: PageOptions<B> = {},
): Promise<ReadPage<B>> {
  const { schema, ...requestOptions } = options;
  const { body, response } = await requestWithResponse<unknown>(path, requestOptions);
  const page = schema ? parseResponse(schema, body, `GET ${path}`) : (body as B);
  const next = nextPagePath(
    response.headers,
    response.url,
    BASE_URL,
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );
  return { ...page, next };
}

/**
 * GET every row of a list, following `rel="next"` until a page carries none.
 * A next link naming a page already read is a server defect that would never
 * end, so it throws instead of looping.
 */
async function getAllItems<B extends { items: readonly unknown[] }>(
  path: string,
  options: PageOptions<B> = {},
): Promise<B["items"][number][]> {
  const items: B["items"][number][] = [];
  const read = new Set<string>();
  for (let next: string | null = path; next !== null; ) {
    if (read.has(next)) {
      throw new Error(`Link rel="next" on ${path} names ${next} again`);
    }
    read.add(next);
    const page: ReadPage<B> = await getPage(next, options);
    items.push(...page.items);
    next = page.next;
  }
  return items;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, options),

  getPage,

  getAllItems,

  getNDJSON,

  getJSONWithTotal,

  getWithETag,

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "POST",
      body:
        body instanceof FormData
          ? body
          : body
            ? JSON.stringify(body)
            : undefined,
      headers: body instanceof FormData ? {} : undefined, // Don't set Content-Type for FormData
      ...options,
    }),

  put: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
      ...options,
    }),

  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
      ...options,
    }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { method: "DELETE", ...options }),
};
